import { useGetUserInfoQuery } from "../../../app/rootReducer";
import { JSX } from "react";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";

export const CompanyTab = (): JSX.Element => {
    const userData = useGetUserInfoQuery();

    const { activeLanguage, dealer } = userData?.data || {
        activeLanguage: null,
    };

    const {
        logo: companyLogo,
        name: companyName,
        email: companyEmail,
        phoneNumber: companyPhoneNumber,
        address: companyAddress,
    } = dealer || {};
    const { street, postal_code, city } = companyAddress || {};

    const { t, isLoading: isTranslationsLoading } = useStringTranslation();
    const translationsLoaded = !isTranslationsLoading && !!activeLanguage;

    return (
        <>
            <div className="c-account-content c-account-content--company-info">
                <div className="c-account-content__header">
                    {translationsLoaded ? (
                        <h1>{t("company-information")}</h1>
                    ) : (
                        <h1 className="skeleton skeleton-title"></h1>
                    )}
                </div>
                <div className="c-account-content__wrapper">
                    <div className="c-account-content__column">
                        {translationsLoaded ? (
                            <span className="c-account-content__column__text c-account-content__column__text--label">
                                {t("company")}
                            </span>
                        ) : (
                            <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                        )}

                        {translationsLoaded ? (
                            <>
                                {companyName ? (
                                    <span className="c-account-content__column__text">
                                        {companyName}
                                    </span>
                                ) : (
                                    <span className="c-account-content__column__text">
                                        -
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="c-account-content__column__text skeleton skeleton-name"></span>
                        )}
                    </div>

                    <div className="c-account-content__column c-account-content__column--logo">
                        {translationsLoaded ? (
                            <span className="c-account-content__column__text c-account-content__column__text--label">
                                {t("logo")}
                            </span>
                        ) : (
                            <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                        )}

                        {translationsLoaded ? (
                            <>
                                {!!companyLogo && (
                                    <div className="c-account-content__column__logo">
                                        <img
                                            src={companyLogo ?? ""}
                                            alt="logo"
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="c-account-content__column__logo skeleton skeleton-carousel"></div>
                        )}
                    </div>
                </div>
            </div>

            <div className="c-account-content c-account-content--company-location">
                <div className="c-account-content__header">
                    {translationsLoaded ? (
                        <h1>{t("location")}</h1>
                    ) : (
                        <h1 className="skeleton skeleton-title"></h1>
                    )}
                </div>
                <div className="c-account-content__wrapper">
                    <div className="c-account-content__column">
                        {translationsLoaded ? (
                            <span className="c-account-content__column__text c-account-content__column__text--label">
                                {t("location-name")}
                            </span>
                        ) : (
                            <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                        )}

                        {translationsLoaded ? (
                            <>
                                {companyName ? (
                                    <span className="c-account-content__column__text">
                                        {companyName}
                                    </span>
                                ) : (
                                    <span className="c-account-content__column__text">
                                        -
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="c-account-content__column__text skeleton skeleton-name"></span>
                        )}
                    </div>

                    <div className="c-account-content__column">
                        {translationsLoaded ? (
                            <span className="c-account-content__column__text c-account-content__column__text--label">
                                {t("general-email-addresses")}
                            </span>
                        ) : (
                            <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                        )}

                        {translationsLoaded ? (
                            <>
                                {companyEmail ? (
                                    <span className="c-account-content__column__text">
                                        {companyEmail}
                                    </span>
                                ) : (
                                    <span className="c-account-content__column__text">
                                        -
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="c-account-content__column__text skeleton skeleton-name"></span>
                        )}
                    </div>

                    <div className="c-account-content__column">
                        {translationsLoaded ? (
                            <span className="c-account-content__column__text c-account-content__column__text--label">
                                {t("general-phone-number")}
                            </span>
                        ) : (
                            <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                        )}

                        {translationsLoaded ? (
                            <>
                                {companyPhoneNumber ? (
                                    <span className="c-account-content__column__text">
                                        {companyPhoneNumber}
                                    </span>
                                ) : (
                                    <span className="c-account-content__column__text">
                                        -
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="c-account-content__column__text skeleton skeleton-name"></span>
                        )}
                    </div>
                </div>

                {!!companyAddress && (
                    <div className="c-account-content__column__sub-block">
                        <div className="c-account-content__header">
                            {translationsLoaded ? (
                                <h3>{t("addresses")}</h3>
                            ) : (
                                <h3 className="skeleton skeleton-title"></h3>
                            )}
                        </div>

                        <div className="c-account-content__column">
                            {translationsLoaded ? (
                                <span className="c-account-content__column__text c-account-content__column__text--label">
                                    {t("visiting-address")}
                                </span>
                            ) : (
                                <span className="c-account-content__column__text c-account-content__column__text--label skeleton skeleton-name"></span>
                            )}

                            {translationsLoaded ? (
                                <>
                                    {!!street || !!postal_code || !!city ? (
                                        <span className="c-account-content__column__text">
                                            {street}, {postal_code}, {city}
                                        </span>
                                    ) : (
                                        <span className="c-account-content__column__text">
                                            -
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="c-account-content__column__text skeleton skeleton-name"></span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
